import React from 'react';
import { motion } from 'framer-motion';
import { SlackThreadBackground } from './signals/SlackThreadBackground';
import { EngagementThread } from './signals/EngagementThread';
import { BurnoutCard } from './signals/BurnoutCard';
import { BurnoutThread } from './signals/BurnoutThread';
import { Button } from './ui/Button';
import { EmailCaptureForm } from './ui/EmailCaptureForm';
interface SignalsSectionProps {
  onGetStarted: (email: string) => void;
  isSubmitted: boolean;
  submittedEmail: string;
}
const signals = [{
  id: 1,
  title: 'Trust & Psychological Safety',
  impact: "Harvard & Google research rank psychological safety as the #1 driver of high-performing teams. We combine multiple research-grounded indicators to surface clear insights into your team's trust and psychological safety.",
  component: SlackThreadBackground,
  metric: {
    value: '87%',
    label: 'High',
    trend: '+1%',
    color: 'bg-teal-500',
    textColor: 'text-teal-600'
  }
}, {
  id: 2,
  title: 'Engagement & Morale',
  impact: 'Highly engaged employees deliver higher output and stay longer—Gallup calls it "discretionary effort". Our research-backed signals show whether your culture is fueling that motivation or letting it fade.',
  component: EngagementThread,
  metric: {
    value: '74%',
    label: 'Rising',
    trend: '+5%',
    color: 'bg-[#8679DF]',
    textColor: 'text-[#8679DF]'
  }
}, {
  id: 3,
  title: 'Burnout & Retention Risk',
  impact: 'The Maslach–Leiter model shows burnout erodes productivity long before resignations hit, so our research-backed signals spotlight rising stress, exhaustion, and withdrawal early—letting you act where relief is needed and keep your best people onboard.',
  image: 'https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?auto=format&fit=crop&q=80&w=1000',
  metric: {
    value: '18%',
    label: 'Low',
    trend: '-4%',
    color: 'bg-teal-500',
    textColor: 'text-teal-600'
  }
}];
const MetricCard = ({
  metric
}) => <div className="bg-white rounded-lg shadow-lg p-4" data-id="element-260">
    <div className="flex items-center justify-between" data-id="element-261">
      <div className="text-2xl font-bold text-[#8679DF]" data-id="element-262">{metric.value}</div>
      <div className={`px-2 py-0.5 text-xs font-semibold rounded-full ${metric.color} bg-opacity-20 ${metric.textColor}`} data-id="element-263">
        {metric.label}
      </div>
    </div>
  </div>;
export const SignalsSection = ({
  onGetStarted,
  isSubmitted,
  submittedEmail
}: SignalsSectionProps) => {
  return <section id="signals" className="bg-[#F8F7FF]" data-id="element-264">
      <div className="text-center py-24" data-id="element-265">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-266">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 md:mb-8 tracking-[-0.02em]" style={{
          fontSize: 'clamp(2.25rem, 5vw, 3.75rem)',
          lineHeight: '1.1'
        }} data-id="element-267">
            Signals That Drive Team Health
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium" style={{
          fontSize: 'clamp(1.125rem, 1.5vw + 0.5rem, 1.5rem)'
        }} data-id="element-268">
            We translate leading organizational-psychology research into
            traffic-light indicators you can act on in real time.
          </p>
        </div>
      </div>
      {signals.map((signal, index) => <div key={signal.id} className="py-16 md:py-24 border-t border-gray-100" data-id="element-269">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-270">
            <div className="flex flex-col md:flex-row gap-8 md:gap-16" data-id="element-271">
              {/* Text Content - Now comes first on mobile */}
              <motion.div className={`w-full md:w-[55%] flex flex-col justify-center order-1 ${index % 2 === 1 ? 'md:order-2' : 'md:order-1'}`} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-272">
                <h3 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900 leading-tight" data-id="element-273">
                  {signal.title}
                </h3>
                <div className="space-y-4" data-id="element-274">
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-275">
                    {signal.impact}
                  </p>
                  {signal.description && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-276">
                      {signal.description}
                    </p>}
                </div>
              </motion.div>
              {/* Image Content - Comes second on mobile */}
              <motion.div className={`w-full md:w-[45%] order-2 ${index % 2 === 1 ? 'md:order-1' : 'md:order-2'}`} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.8
          }} data-id="element-277">
                <div className="relative h-[400px] rounded-xl overflow-hidden shadow-xl" data-id="element-278">
                  {signal.id === 1 && <SlackThreadBackground data-id="element-279" />}
                  {signal.id === 2 && <EngagementThread data-id="element-280" />}
                  {signal.id === 3 && <div className="w-full h-full bg-white relative" data-id="element-281">
                      <BurnoutThread data-id="element-282" />
                    </div>}
                </div>
              </motion.div>
            </div>
          </div>
        </div>)}
    </section>;
};